import { useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { GRID_SIZE_PX } from '@/constants/canvas';
import type { BlockGroup as Group, WorkflowBlock } from '@/types/workflow';
import { dispatchOnboardingEvent } from '@/components/onboarding/uiAdapter';
import {
  inferParentGroupIds,
  normalizeBlockOwnership,
  computeDepth,
  getLeafGroupIds,
  computeCollapsedCenter,
} from '@/lib/groupHierarchy';

const snapToGrid = (value: number): number => Math.round(value / GRID_SIZE_PX) * GRID_SIZE_PX;

export interface UseGroupHandlersDeps {
  blocks: WorkflowBlock[];
  groups: Group[];
  groupMode: boolean;
  groupSelection: string[];
  currentLayerGroupId: string | null;
  setSelectedBlockId: (id: string | null) => void;
  setSelectedGroupId: (id: string | null) => void;
  /** Selection-mutex: välja en grupp rensar connector-highlight + panel. */
  setSelectedConnectorId: (id: string | null) => void;
  setGroupMode: (v: boolean | ((prev: boolean) => boolean)) => void;
  setGroupSelection: (ids: string[] | ((prev: string[]) => string[])) => void;
  setConnectMode: (v: boolean) => void;
  createGroup: (blockIds: string[], color: string, parent?: string) => void;
  updateGroup: (id: string, patch: Partial<Group>) => void;
  updateGroups: (updater: (groups: Group[]) => Group[]) => void;
  logAction: (action: string, detail?: string) => void;
  t: (key: string) => string;
  tCount: (key: string, count: number) => string;
}

export function useGroupHandlers(deps: UseGroupHandlersDeps) {
  const {
    blocks, groups, groupMode, groupSelection, currentLayerGroupId,
    setSelectedBlockId, setSelectedGroupId, setSelectedConnectorId,
    setGroupMode, setGroupSelection, setConnectMode,
    createGroup, updateGroup, updateGroups, logAction, t, tCount,
  } = deps;

  const handleToggleConnectMode = useCallback(() => {
    setConnectMode(true);
    setGroupMode(false);
    setGroupSelection([]);
  }, [setConnectMode, setGroupMode, setGroupSelection]);

  const handleToggleGroupMode = useCallback(() => {
    if (groupMode && groupSelection.length >= 2) {
      createGroup(groupSelection, '#8b5cf6', currentLayerGroupId ?? undefined);
      logAction('Group created', `${groupSelection.length} blocks grouped`);
      toast({ title: t('groupCreated'), description: tCount('blocksGroupedCount', groupSelection.length) });
      dispatchOnboardingEvent({ name: 'group:created', data: { count: groupSelection.length } });
    }
    setGroupMode(prev => !prev);
    setGroupSelection([]);
    setConnectMode(false);
  }, [
    groupMode, groupSelection, createGroup, currentLayerGroupId,
    setGroupMode, setGroupSelection, setConnectMode, logAction, t, tCount,
  ]);

  /**
   * Ad-hoc group creation used by the right-click context menu. Creates a
   * group from an explicit set of block IDs without going through
   * group-mode state. Safe to call whenever at least 2 blocks are selected.
   */
  const handleCreateGroupFromIds = useCallback((blockIds: string[]) => {
    // Allow single-block groups — useful for quickly converting any
    // block into a group that can later be collapsed to a layer.
    if (blockIds.length < 1) return;
    createGroup(blockIds, '#8b5cf6', currentLayerGroupId ?? undefined);
    logAction('Group created', `${blockIds.length} blocks grouped`);
    toast({ title: t('groupCreated'), description: tCount('blocksGroupedCount', blockIds.length) });
    dispatchOnboardingEvent({ name: 'group:created', data: { count: blockIds.length } });
    setGroupSelection([]);
    setGroupMode(false);
  }, [
    createGroup, currentLayerGroupId, logAction, t, tCount,
    setGroupSelection, setGroupMode,
  ]);

  /**
   * Add a block to an existing group (right-click → "Add to group" submenu).
   */
  const handleAddBlockToGroup = useCallback((groupId: string, blockId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    if (group.blockIds.includes(blockId)) return;
    updateGroup(groupId, { blockIds: [...group.blockIds, blockId] });
    logAction('Block added to group', `${blockId} → ${group.name ?? groupId}`);
  }, [groups, updateGroup, logAction]);

  const handleToggleGroupSelection = useCallback((id: string) => {
    setGroupSelection(prev =>
      prev.includes(id) ? prev.filter(blockId => blockId !== id) : [...prev, id],
    );
  }, [setGroupSelection]);

  const handleToggleGroupCollapse = useCallback((id: string) => {
    const group = groups.find(g => g.id === id);
    if (group) updateGroup(id, { collapsed: !group.collapsed });
  }, [groups, updateGroup]);

  const handleUpdateGroupPosition = useCallback((id: string, x: number, y: number) => {
    updateGroup(id, { collapsedX: snapToGrid(x), collapsedY: snapToGrid(y) });
  }, [updateGroup]);

  const handleUpdateGroupColor = useCallback((id: string, color: string) => {
    updateGroup(id, { color });
  }, [updateGroup]);

  const handleToggleGroupLock = useCallback((id: string) => {
    const group = groups.find(g => g.id === id);
    if (group) updateGroup(id, { locked: !group.locked });
  }, [groups, updateGroup]);

  const handleSelectGroup = useCallback((id: string | null) => {
    setSelectedGroupId(id);
    if (id) {
      setSelectedBlockId(null);
      // Defensiv rensning — context-versionen av setSelectedGroupId har
      // redan en mutex som rensar selectedConnectorId när id≠null, men
      // vi kan inte garantera att call-siten alltid passar in just den
      // settern. Kostar inget extra render om värdet redan är null.
      setSelectedConnectorId(null);
    }
  }, [setSelectedBlockId, setSelectedGroupId, setSelectedConnectorId]);

  /**
   * Normalize parent-child relationships (from blockIds overlap) and set
   * collapsed=true on every group. Deepest groups collapse first — the
   * update is atomic via updateGroups, so the user only sees the final
   * state; the "deepest first" ordering is preserved in the resulting
   * group array so undo/redo and persistence follow the hierarchy.
   */
  const handleCollapseAll = useCallback(() => {
    updateGroups(prev => {
      if (prev.length === 0) return prev;
      const parentMap = inferParentGroupIds(prev);
      const normalized = normalizeBlockOwnership(prev, parentMap);
      const ordered = [...normalized].sort(
        (a, b) => computeDepth(b.id, parentMap) - computeDepth(a.id, parentMap),
      );
      return ordered.map(g => {
        if (g.collapsed) return g;
        const hasPos = g.collapsedX != null && g.collapsedY != null;
        const { x, y } = hasPos
          ? { x: g.collapsedX!, y: g.collapsedY! }
          : computeCollapsedCenter(g.id, normalized, parentMap, blocks);
        return { ...g, collapsed: true, collapsedX: snapToGrid(x), collapsedY: snapToGrid(y) };
      });
    });
    logAction('Collapse all groups', `${groups.length} groups`);
    toast({ title: t('collapseAll'), description: tCount('blocksGroupedCount', groups.length) });
  }, [blocks, groups.length, updateGroups, logAction, t, tCount]);

  /**
   * Collapse only leaf groups (groups with no children) to layers. Parent
   * groups remain visible frames around the collapsed leaves + any loose
   * blocks they still own after normalization.
   */
  const handleCollapseToLayers = useCallback(() => {
    updateGroups(prev => {
      if (prev.length === 0) return prev;
      const parentMap = inferParentGroupIds(prev);
      const normalized = normalizeBlockOwnership(prev, parentMap);
      const leaves = getLeafGroupIds(normalized, parentMap);
      return normalized.map(g => {
        if (!leaves.has(g.id)) {
          return g.collapsed ? { ...g, collapsed: false } : g;
        }
        if (g.collapsed) return g;
        const hasPos = g.collapsedX != null && g.collapsedY != null;
        const { x, y } = hasPos
          ? { x: g.collapsedX!, y: g.collapsedY! }
          : computeCollapsedCenter(g.id, normalized, parentMap, blocks);
        return { ...g, collapsed: true, collapsedX: snapToGrid(x), collapsedY: snapToGrid(y) };
      });
    });
    logAction('Collapse to layers', `${groups.length} groups`);
    toast({ title: t('collapseToLayers') });
  }, [blocks, groups.length, updateGroups, logAction, t]);

  const handleExpandAll = useCallback(() => {
    updateGroups(prev => prev.map(g => (g.collapsed ? { ...g, collapsed: false } : g)));
    logAction('Expand all groups', `${groups.length} groups`);
    toast({ title: t('expandAll') });
  }, [groups.length, updateGroups, logAction, t]);

  return {
    handleToggleConnectMode,
    handleToggleGroupMode,
    handleToggleGroupSelection,
    handleToggleGroupCollapse,
    handleUpdateGroupPosition,
    handleUpdateGroupColor,
    handleToggleGroupLock,
    handleSelectGroup,
    handleCreateGroupFromIds,
    handleAddBlockToGroup,
    handleCollapseAll,
    handleCollapseToLayers,
    handleExpandAll,
  };
}
